import Button from 'Components/Base/Button'
import Column from 'Components/Base/Column'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import useAppointments from 'hooks/useAppointments'
import useNewEnrollment from 'hooks/useNewEnrollment'
import React, { useEffect, useState } from 'react'
import Calendar from 'react-calendar'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from 'reducers/authSlice'
import { setLoading } from 'reducers/tempSlice'
import styled from 'styled-components'
import { AppointmentDate, AppointmentRequest, AppointmentResponse, AppointmentTime } from 'types/Appointment'
import './CalendarStyles.css'

interface props {
  ctl: boolean
  isInitial: boolean
  handleSubmit: Function
}

export default function CalendarRight({ ctl, isInitial, handleSubmit }: props) {
  const [date, setDate] = useState(new Date())
  const [selectedTime, setSelectedTime] = useState<string>('')
  const [helpText, setHelpText] = useState<string>('')
  const [earlier, setEarlier] = useState<boolean>(false)
  const [availableAppointments, setAvailableAppointments] = useState<AppointmentDate[]>([])
  const [availableTimes, setAvailableTimes] = useState<AppointmentTime[]>([])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authToken = useAppSelector(getAccessToken)
  const { getInitialAppointments, postInitialAppointment } = useNewEnrollment({ token: authToken })
  const { getAppointments, getCTLAppointments, postAppointment, postCTLAppointment } = useAppointments(authToken!)
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentTime>()
  const [rescheduleInitial, setRescheduleInitial] = useState<boolean>(false)
  const [scheduleId, setScheduleId] = useState<number>()
  const filterEvent = { target: { value: '6, 21' } }
  const [disabled, setDisabled] = useState<boolean>(false)

  useEffect(() => {
    setAvailableTimes(availableAppointments.find((a) => a.date === dayjs(date).format('MM/DD/YYYY'))?.appointmentTimes ?? [])
  }, [date])

  useEffect(() => {
    dispatch(setLoading(true))
    if (isInitial) {
      getInitialAppointments(ctl)
        .then((res: AxiosResponse<AppointmentResponse>) => {
          const dates = res.data.appointmentDates.map((date) =>
            Object.assign({}, date, {
              date: dayjs(date.date).format(`MM/DD/YYYY`),
            }),
          )
          setScheduleId(res.data.scheduleId)
          setDate(new Date(dates.find((d) => d.appointmentTimes.length > 0)?.date ?? ''))
          setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
          setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
          setAvailableAppointments(dates)
          dispatch(setLoading(false))
        })
        .catch(() => {
          dispatch(setLoading(false))
          navigate('/error')
        })
    } else {
      if (ctl) {
        getCTLAppointments()
          .then((res: AxiosResponse<AppointmentResponse>) => {
            if (!res.data.canReschedule && !res.data.canSchedule) {
              setHelpText(res.data.message)
              dispatch(setLoading(false))
            } else {
              const dates = res.data.appointmentDates.map((date) =>
                Object.assign({}, date, {
                  date: dayjs(date.date).format(`MM/DD/YYYY`),
                }),
              )
              setRescheduleInitial(res.data.isInitial)
              setScheduleId(res.data.scheduleId)
              setDate(new Date(dates.find((d) => d.appointmentTimes.length > 0)?.date ?? ''))
              setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
              setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
              setAvailableAppointments(dates)
              dispatch(setLoading(false))
            }
          })
          .catch(() => {
            dispatch(setLoading(false))
          })
      } else {
        getAppointments()
          .then((res: AxiosResponse<AppointmentResponse>) => {
            if (!res.data.canReschedule && !res.data.canSchedule) {
              setHelpText(res.data.message)
              dispatch(setLoading(false))
            } else {
              const dates = res.data.appointmentDates.map((date) =>
                Object.assign({}, date, {
                  date: dayjs(date.date).format(`MM/DD/YYYY`),
                }),
              )
              setRescheduleInitial(res.data.isInitial)
              setScheduleId(res.data.scheduleId)
              setDate(new Date(dates.find((d) => d.appointmentTimes.length > 0)?.date ?? ''))
              setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
              setAvailableTimes(dates.find((d) => d.appointmentTimes.length > 0)?.appointmentTimes ?? [])
              setAvailableAppointments(dates)
              dispatch(setLoading(false))
            }
          })
          .catch(() => {
            dispatch(setLoading(false))
          })
      }
    }
  }, [])

  const checkAvailability = (date: any) => {
    const selected = availableAppointments.find((x) => {
      const splitApp = x.appointmentTimes[0].startTime.split('T')
      return dayjs(splitApp[0]).format('MM/DD/YYYY') === dayjs(date).format('MM/DD/YYYY')
    })
    const selected2 = selected?.appointmentTimes.find(
      (x) =>
        parseInt(dayjs(x.startTime).format('HH')) >= parseInt(filterEvent.target.value.split(',')[0]) &&
        parseInt(dayjs(x.startTime).format('HH')) < parseInt(filterEvent.target.value.split(',')[1]),
    )
    return selected2
  }

  const handleSelectTime = (time: string) => {
    setSelectedTime(time)
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
  })
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  const parts = formatter.formatToParts(new Date())
  const timeZone1 = timeZone + ' ' + (parts.find((part) => part.type === 'timeZoneName')?.value ?? '')

  const onSubmit = () => {
    if (selectedAppointment) {
      setDisabled(true)
      const { startTime, endTime, pharmacistGuid } = selectedAppointment
      const startTimeSplit = startTime.split('-')
      startTimeSplit.pop()
      const endTimeSplit = endTime.split('-')
      endTimeSplit.pop()
      const appointment: AppointmentRequest = {
        pharmacist: pharmacistGuid,
        startTime: startTimeSplit.join('-'),
        endTime: endTimeSplit.join('-'),
        immediateAppointment: earlier,
        scheduleId: scheduleId,
      }
      if (isInitial) {
        postInitialAppointment(appointment, ctl)
          .then((res) => {
            if (!res.data.success) {
              navigate('/error')
            }
          })
          .catch(() => {
            navigate('/error')
          })
          .finally(() => {
            dispatch(setLoading(false))
            handleSubmit()
          })
      } else {
        if (ctl) {
          postCTLAppointment(appointment, rescheduleInitial)
            .then((res) => {
              if (!res.data.success) {
                navigate('/error')
              }
            })
            .catch(() => {
              navigate('/error')
            })
            .finally(() => {
              dispatch(setLoading(false))
              handleSubmit()
            })
        } else {
          postAppointment(appointment, rescheduleInitial)
            .then((res) => {
              if (!res.data.success) {
                navigate('/error')
              }
            })
            .catch(() => {
              navigate('/error')
            })
            .finally(() => {
              dispatch(setLoading(false))
              handleSubmit()
            })
        }
      }
    }
  }

  return (
    <Container>
      {helpText.length > 0 ? (
        <Column>
          <Typography>Sorry! Scheduling is unavailable at this time</Typography>
          <Typography>{helpText}</Typography>
        </Column>
      ) : (
        <>
          <Column>
            <Calendar
              onChange={setDate}
              value={date}
              tileClassName={({ date }) => {
                const hasTimes = checkAvailability(date)
                return !hasTimes ? 'no-times' : 'has-times' // no class for no-times
              }}
              calendarType="US" // Sunday-first
              locale="en-US"
              prev2Label={null} // removes the "previous year" chevron
              next2Label={null}
              //tileDisabled={({ date }) => !checkHasTimes(date)}
            />

            <TLText>
              <b>Time zone</b>
            </TLText>
            <Typography styles={{ marginTop: -10 }}>
              <Icon name="earth" styles={{ marginRight: 5 }} />
              {timeZone1}
            </Typography>
            <TLText style={{ marginTop: 15 }}>
              <b>Language</b>
            </TLText>
            <DropdownWrapper style={{ marginTop: -10 }}>
              <Select>
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </Select>
              <Arrow>
                <Icon name="chevron-down" />
              </Arrow>
            </DropdownWrapper>
            <Row styles={{ maxWidth: 300, marginTop: 25 }}>
              <Checkbox type="checkbox" checked={earlier} onChange={() => setEarlier(!earlier)}></Checkbox>
              <EarlierText>I’d like to be contacted if an earlier appointment becomes available</EarlierText>
            </Row>
          </Column>
          <Column>
            <Typography styles={{ marginBottom: 20 }}>{dayjs(date).format('dddd, MMMM D')}</Typography>
            <TimeScroll>
              {availableTimes.length > 0 &&
                availableTimes.map((app: AppointmentTime, i) => (
                  <div key={i}>
                    {selectedTime === app.startTime ? (
                      <Row>
                        <TimeSelectedCont
                          onClick={() => {
                            handleSelectTime(app.startTime)
                            setSelectedAppointment(app)
                          }}
                        >
                          {dayjs(app.startTime).format('h:mma')}
                        </TimeSelectedCont>
                        {selectedTime === app.startTime && (
                          <Button disabled={disabled} text="Next" styles={{ height: 46, marginLeft: 5 }} onClick={() => onSubmit()} />
                        )}
                      </Row>
                    ) : (
                      <TimeCont
                        onClick={() => {
                          handleSelectTime(app.startTime)
                          setSelectedAppointment(app)
                        }}
                      >
                        {dayjs(app.startTime).format('h:mma')}
                      </TimeCont>
                    )}
                  </div>
                ))}
            </TimeScroll>
          </Column>
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  text-align: left;
  max-width: 1200px;
  min-width: 750px;
  margin: 40px auto 0px;
  justify-content: space-between;
  @media only screen and (max-width: 600px) {
    margin: 0px auto 0px;
    padding: 30px 20px;
    flex-direction: column;
    max-width: 95%;
    min-width: 95%;
  }
`

const TimeCont = styled.div`
  padding: 10px 59px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.primary1};
  border-radius: 5px;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.colors.primary1};
  font-weight: 700;
  cursor: pointer;
`
const TimeSelectedCont = styled.div`
  padding: 10px 20px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.primary1};
  border-radius: 5px;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.colors.primary1};
  font-weight: 700;
  cursor: pointer;
  @media only screen and (max-width: 600px) {
    width: 75%;
    padding: 10px 50px;
  }
`

const TimeScroll = styled.div`
  overflow-y: scroll;
  padding-right: 40px;
  max-height: 550px;
  @media only screen and (max-width: 600px) {
    padding: 30px 20px;
    flex-direction: column;
    max-width: 95%;
  }
`

const TLText = styled.p`
  margin-top: 30px;
`

const DropdownWrapper = styled.div`
  position: relative;
  width: fit-content; /* adjust as needed */
`

const Select = styled.select`
  width: fit-content;
  padding: 10px 40px 10px 12px; /* space for arrow */
  font-size: 16px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  appearance: none; /* remove default arrow */
  background-color: #fff;
  color: #333;
  outline: none;
  cursor: pointer;
`

const Arrow = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  color: #666;
`

const Checkbox = styled.input`
  width: 15px;
  height: 15px;
  accent-color: #00677f;
  margin: 0px 8px auto 5px;

  /* Optional: make it even larger visually */
`

const EarlierText = styled.p`
  font-size: 16px;
  font-weight: 700;
  margin-top: -4px;
  color: #00677f;
`
